import { useState, useCallback } from 'react';
import { useAppStore } from '../../store/appStore';
import { useNavigate } from 'react-router-dom';

interface ResetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ONBOARDING_COMPLETE_KEY = 'onboardingComplete';

export function ResetDialog({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: ResetDialogProps) {
  const [isResetting, setIsResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const clearAllData = useAppStore((state) => state.clearAllData);
  const navigate = useNavigate();

  const CONFIRM_WORD = 'BORRAR';
  const canReset = confirmText === CONFIRM_WORD;

  const resetAndClose = useCallback(() => {
    setConfirmText('');
    setIsResetting(false);
    onClose();
  }, [onClose]);

  const handleReset = useCallback(async () => {
    if (!canReset) return;

    setIsResetting(true);

    try {
      await clearAllData();
      localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
      onSuccess('Todos los datos han sido eliminados');
      resetAndClose();
      navigate('/');
      window.location.reload();
    } catch {
      onError('Error al eliminar los datos');
      setIsResetting(false);
    }
  }, [canReset, clearAllData, onSuccess, onError, resetAndClose, navigate]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={resetAndClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-dialog-title"
      >
        <h2
          id="reset-dialog-title"
          className="text-xl font-bold text-near-black mb-4"
        >
          Reiniciar datos
        </h2>

        {isResetting ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <span className="ml-3 text-mid-gray">Eliminando datos...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded">
              <span className="text-red-600 text-lg">⚠️</span>
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-1">
                  Esta accion no se puede deshacer
                </p>
                <p>
                  Se eliminaran todas las transacciones, presupuestos y
                  correcciones. Las categorias predeterminadas se restauraran.
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="reset-confirm"
                className="block text-sm text-mid-gray mb-2"
              >
                Escribe <strong>{CONFIRM_WORD}</strong> para confirmar:
              </label>
              <input
                id="reset-confirm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder={CONFIRM_WORD}
                autoComplete="off"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 text-mid-gray hover:text-near-black transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleReset}
                disabled={!canReset}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reiniciar todo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
